import { memo } from 'react';
import type { Mutators } from 'final-form-arrays';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';

import { IKnmFormAttachment } from '../interfaces/IKnmFormAttachment';
import styles from './KnmAttachmentsList.module.scss';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { START_DATE_BLOCK_ID } from '../constants/fieldsElementsIds';
import { DatePickerField } from '../../../UIPackage/MainModule/components/DatePickerField';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';

interface IKnmAttachmentsListProps {
  isShowDateTime?: boolean;
  isRequired?: boolean;
  isWatchOnly?: boolean;
  name: string;
  removeField: Mutators['remove'] | undefined;
}

export const KnmAttachmentsList = memo(
  ({ isShowDateTime, isRequired, isWatchOnly, name, removeField }: IKnmAttachmentsListProps) => {
    const { downloadKnmDocumentAttachment } = useKnmDocumentAttachment();
    const downloadAttachment = (attachment: IKnmFormAttachment) => () => {
      const { fileName, fileSize } = attachment;
      const filePath = attachment.filePath || null;
      const mimeType = attachment.mimeType || null;

      downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
    };
    const makeRemoveHandler = (index: number) => () => !isWatchOnly && removeField && removeField(name, index);

    return (
      <FieldArray name={name} validate={isRequired ? makeRequired('Поле не может быть пустым') : undefined}>
        {({ fields: attachments }: FieldArrayRenderProps<IKnmFormAttachment, HTMLElement>) => {
          if (!attachments.length) {
            return null;
          }

          return (
            <ul className={styles.AttachmentsList} style={{ width: `100%` }}>
              {attachments.map((attachmentsFieldName, attachmentIndex) => {
                const attachmentFieldValue: IKnmFormAttachment = attachments.value?.[attachmentIndex];
                let progress: number | undefined;

                if (attachmentFieldValue?.isNew) {
                  progress = attachmentFieldValue?.dateCreate ? 100 : 0;
                }

                return (
                  <li className={styles.Attachment} key={attachmentsFieldName}>
                    <Row notWrap>
                      <FieldBlock size="half">
                        <UploadFile
                          fileName={attachmentFieldValue?.fileName || ''}
                          fileSize={attachmentFieldValue?.fileSize}
                          progress={progress}
                          onFileNameClick={
                            !attachmentFieldValue?.isNew && attachmentFieldValue?.guid
                              ? downloadAttachment(attachmentFieldValue)
                              : undefined
                          }
                          onRemove={!isWatchOnly ? makeRemoveHandler(attachmentIndex) : undefined}
                        />
                      </FieldBlock>

                      {isShowDateTime && (
                        <FieldBlock
                          label="Дата и время загрузки файла"
                          id={START_DATE_BLOCK_ID}
                          color="black"
                          placementLabel="left"
                          size="half"
                        >
                          <DatePickerField
                            className="shared-row-field"
                            name={`${name}[${attachmentIndex}].dateCreate`}
                            id={`${name}[${attachmentIndex}].dateCreate`}
                            timeFormat
                            disabled
                          />
                        </FieldBlock>
                      )}
                    </Row>
                  </li>
                );
              })}
            </ul>
          );
        }}
      </FieldArray>
    );
  },
);
